import { useState, useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, Button, Badge, toast, Input } from '../index';
import {
  ActiveLoginService,
  type ActiveLoginSession,
  UserService,
  CommissionService,
  StudentProfileService,
} from '@repo/api-sdk';
import {
  Laptop,
  Smartphone,
  Globe,
  Trash2,
  ShieldAlert,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Target,
  Clock,
  AlertTriangle,
  Percent,
  GraduationCap,
  ArrowUp,
  ArrowDown,
  Calendar,
  Camera,
} from 'lucide-react';
import { useAuth } from '@repo/auth';
import { DialogIds, useDialog } from '../hooks/use-dialog';

export const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, portal, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const { open: openDeleteConfirm } = useDialog(DialogIds.DELETE_CONFIRM);

  // Extract Sales Role ID if available
  const salesRoleId = (user as any)?.roles?.find(
    (r: any) => r.role === 'Sales' || r.role === 'SalesManager'
  )?._id;

  const { data: progressResponse } = useQuery({
    queryKey: ['commission-progress', salesRoleId],
    queryFn: () => CommissionService.getAgentProgress(salesRoleId!),
    enabled: portal === 'sales' && !!salesRoleId,
  });
  const progressData = progressResponse;
  const studentProfileKey = (user as any)?.id || (user as any)?._id;

  // Real Profile Fetching for Student Portal
  const { data: profileData } = useQuery({
    queryKey: ['student-profile', studentProfileKey],
    queryFn: () => StudentProfileService.getProfile(),
    enabled: portal === 'student' && !!studentProfileKey,
  });

  const displayUser = portal === 'student' && profileData ? profileData : user;
  const getRoleLabel = (roleObj: any) => {
    if (typeof roleObj === 'string') return roleObj;
    if (roleObj?.role) return roleObj.role;
    if (portal === 'student') return 'student';
    return 'N/A';
  };

  // Profile Image Update logic
  const fileInputRef = useRef<HTMLInputElement>(null);
  const updateProfileImageMutation = useMutation({
    mutationFn: (formData: FormData) =>
      portal === 'student'
        ? StudentProfileService.updateProfileImage(formData)
        : UserService.updateProfileImage(formData),
    onSuccess: (response: any) => {
      toast.success(
        t('profile.imageUpdated', { defaultValue: 'Profile image updated successfully' })
      );
      if (portal === 'student') {
        queryClient.invalidateQueries({ queryKey: ['student-profile', studentProfileKey] });
      }
      const nextImage =
        response?.profileImage || response?.user?.profileImage || response?.data?.profileImage;
      if (nextImage) {
        updateUser({ profileImage: nextImage } as any);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('common.error'));
    },
  });

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('profile.imageTooLarge', { defaultValue: 'Image size must be less than 5MB' }));
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    updateProfileImageMutation.mutate(formData);

    // reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  // Password validation function
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) {
      errors.push(t('profile.passwordMinLength'));
    }
    if (!/[A-Z]/.test(password)) {
      errors.push(t('profile.passwordUppercase'));
    }
    if (!/[a-z]/.test(password)) {
      errors.push(t('profile.passwordLowercase'));
    }
    if (!/[0-9]/.test(password)) {
      errors.push(t('profile.passwordNumber'));
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push(t('profile.passwordSpecial'));
    }
    return errors;
  };

  // Calculate password strength
  const getPasswordStrength = (
    password: string
  ): { strength: number; label: string; color: string } => {
    if (!password) return { strength: 0, label: '', color: '' };

    const errors = validatePassword(password);
    const strength = ((5 - errors.length) / 5) * 100;

    if (strength === 100)
      return {
        strength,
        label: t('profile.passwordStrong', { defaultValue: 'Strong' }),
        color: 'bg-green-600 border-green-200',
      };
    if (strength >= 60)
      return {
        strength,
        label: t('profile.passwordMedium', { defaultValue: 'Medium' }),
        color: 'bg-yellow-600 border-yellow-200',
      };
    return {
      strength,
      label: t('profile.passwordWeak', { defaultValue: 'Weak' }),
      color: 'bg-red-600 border-red-200',
    };
  };

  const { data: sessionsResponse, isLoading } = useQuery({
    queryKey: ['active-sessions'],
    queryFn: ActiveLoginService.getActiveSessions,
  });

  // Sort sessions: current session first, then by last activity
  const sessionsData = Array.isArray((sessionsResponse as any)?.data)
    ? (sessionsResponse as any).data
    : Array.isArray((sessionsResponse as any)?.data?.data)
      ? (sessionsResponse as any).data.data
      : [];

  const sortedSessions = sessionsData.length
    ? [...sessionsData].sort((a, b) => {
        if (a.isCurrent) return -1;
        if (b.isCurrent) return 1;
        return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
      })
    : [];

  const terminateSessionMutation = useMutation({
    mutationFn: ActiveLoginService.terminateSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: UserService.changePassword,
    onSuccess: (data) => {
      toast.success(data.message || t('profile.passwordChanged'));
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordErrors([]);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message;
      // Handle array of messages from backend
      const displayMessage = Array.isArray(errorMessage)
        ? errorMessage.join(', ')
        : errorMessage || t('common.error');
      toast.error(displayMessage);
    },
  });

  const terminateAllOtherMutation = useMutation({
    mutationFn: ActiveLoginService.terminateAllOtherSessions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
    },
  });

  const getDeviceIcon = (deviceType: string) => {
    if (
      deviceType?.toLowerCase().includes('mobile') ||
      deviceType?.toLowerCase().includes('android') ||
      deviceType?.toLowerCase().includes('ios')
    ) {
      return <Smartphone className="h-5 w-5" />;
    }
    return <Laptop className="h-5 w-5" />;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateString));
  };

  return (
    <div className="space-y-6">
      {/* User Profile Header (Mobile Style) */}
      <div className="flex flex-col items-center justify-center pt-10 pb-8 px-4 text-center bg-card rounded-[2rem] shadow-sm border border-border/60 relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-linear-to-b from-primary/10 to-transparent" />

        <div className="relative group mx-auto mb-5 z-10 w-28 h-28">
          <div className="h-full w-full rounded-full bg-background flex items-center justify-center p-1.5 shadow-md border border-border/50 relative overflow-hidden">
            <div className="h-full w-full rounded-full bg-primary/10 flex items-center justify-center overflow-hidden relative">
              {(displayUser as any)?.profileImage ? (
                <img
                  src={(displayUser as any).profileImage}
                  alt={displayUser?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-4xl font-black text-primary">
                  {displayUser?.name?.charAt(0) || 'U'}
                </span>
              )}

              {/* Editable Overlay mapped to input */}
              <label className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                {updateProfileImageMutation.isPending ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <Camera className="h-6 w-6 mb-1 text-white" />
                    <span className="text-[10px] font-bold  tracking-wider">
                      {t('profile.change', { defaultValue: 'Change' })}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg, image/png, image/webp"
                      className="hidden"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      disabled={updateProfileImageMutation.isPending}
                    />
                  </>
                )}
              </label>
            </div>
          </div>
          {/* Status dot */}
          <div className="absolute bottom-1 right-1 h-6 w-6 bg-green-500 rounded-full border-4 border-background z-20" />
        </div>

        <h3 className="text-2xl md:text-3xl font-black tracking-tight mb-2 z-10">
          {displayUser?.name || 'N/A'}
        </h3>
        <p className="text-muted-foreground font-medium flex items-center gap-2 mb-6 z-10 text-sm md:text-base">
          <Globe className="h-4 w-4" />
          {displayUser?.email || 'N/A'}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-2 z-10">
          {displayUser?.roles && Array.isArray(displayUser.roles) ? (
            displayUser.roles.map((roleObj: any, index: number) => (
              <Badge
                key={index}
                variant="secondary"
                className={`px-4 py-1.5 text-xs font-bold rounded-full border ${
                  roleObj.isActive
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-red-50 text-red-700 border-red-200'
                }`}
              >
                {getRoleLabel(roleObj)}
              </Badge>
            ))
          ) : (
            <Badge className="px-4 py-1.5 text-xs font-bold rounded-full">
              {(displayUser as any)?.role || 'N/A'}
            </Badge>
          )}
        </div>
      </div>

      {/* Account Details List (Settings Style) */}
      <div className="space-y-4">
        <h4 className="text-sm font-bold  tracking-wider text-muted-foreground px-2">
          {t('profile.personalInfo')}
        </h4>
        <Card className="rounded-[2rem] overflow-hidden shadow-sm border border-border/60">
          <div className="flex flex-col divide-y divide-border/40">
            {/* Phone Row */}
            <div className="flex items-center justify-between p-5 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600">
                  <Smartphone className="h-5 w-5" />
                </div>
                <span className="font-semibold text-sm sm:text-base">{t('common.phone')}</span>
              </div>
              <span dir="ltr" className="text-muted-foreground font-medium text-sm sm:text-base">
                {(displayUser as any)?.phone || 'N/A'}
              </span>
            </div>

            {/* Last Login Row */}
            <div className="flex items-center justify-between p-5 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-600">
                  <Clock className="h-5 w-5" />
                </div>
                <span className="font-semibold text-sm sm:text-base">{t('profile.lastLogin')}</span>
              </div>
              <span className="text-muted-foreground font-medium text-sm sm:text-base">
                {(displayUser as any)?.lastLogin
                  ? formatDate((displayUser as any).lastLogin)
                  : sortedSessions[0]
                    ? formatDate(sortedSessions[0].lastActivityAt)
                    : 'N/A'}
              </span>
            </div>

            {/* Created At Row */}
            <div className="flex items-center justify-between p-5 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600">
                  <Calendar className="h-5 w-5" />
                </div>
                <span className="font-semibold text-sm sm:text-base">
                  {t('profile.accountCreated')}
                </span>
              </div>
              <span className="text-muted-foreground font-medium text-sm sm:text-base">
                {(displayUser as any)?.createdAt
                  ? formatDate((displayUser as any).createdAt)
                  : 'N/A'}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Commission Rules Section (Sales Portal Only) */}
      {portal === 'sales' && progressData?.ruleProgress && progressData.ruleProgress.length > 0 && (
        <Card className="rounded-[2rem] shadow-sm overflow-hidden border border-border/60">
          <CardHeader className="bg-linear-to-r from-primary/5 via-primary/3 to-transparent border-b pb-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary/20">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">
                  {t('sales.dashboard.appliedRules', { defaultValue: 'Applied Commission Rules' })}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('sales.dashboard.rulesDescription', {
                    defaultValue: 'Targets and rules affecting your commission',
                  })}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {progressData.ruleProgress.map((rule: any) => {
                const isGroup = rule.ruleType === 'group_fill';
                const statusConfig = {
                  met: {
                    icon: CheckCircle2,
                    label: t('sales.dashboard.ruleStatus.met', { defaultValue: 'Target Met' }),
                    color: 'text-emerald-600 dark:text-emerald-400',
                    bg: 'bg-emerald-500/10',
                    border: 'border-emerald-500/30',
                  },
                  in_progress: {
                    icon: Clock,
                    label: t('sales.dashboard.ruleStatus.inProgress', {
                      defaultValue: 'In Progress',
                    }),
                    color: 'text-blue-600 dark:text-blue-400',
                    bg: 'bg-blue-500/10',
                    border: 'border-blue-500/30',
                  },
                  not_met: {
                    icon: AlertTriangle,
                    label: t('sales.dashboard.ruleStatus.notMet', { defaultValue: 'Not Met' }),
                    color: 'text-red-600 dark:text-red-400',
                    bg: 'bg-red-500/10',
                    border: 'border-red-500/30',
                  },
                }[rule.status as string] || {
                  icon: Clock,
                  label: rule.status,
                  color: 'text-muted-foreground',
                  bg: 'bg-muted',
                  border: 'border-border',
                };
                const StatusIcon = statusConfig.icon;

                return (
                  <Card
                    key={rule.ruleId}
                    className={`relative overflow-hidden shadow-sm border ${statusConfig.border}`}
                  >
                    <div className={`absolute inset-0 ${statusConfig.bg} opacity-30`} />
                    <CardContent className="p-4 space-y-4 relative z-10">
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-1 min-w-0 flex-1">
                          <div
                            className="font-bold text-foreground truncate text-sm"
                            title={rule.ruleName}
                          >
                            {rule.ruleName}
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className={`${statusConfig.bg} ${statusConfig.color} border ${statusConfig.border} shrink-0 text-[10px] h-5`}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-[10px]">
                        <Badge variant="outline" className="gap-1 h-5 border-muted-foreground/30">
                          {isGroup ? (
                            <>
                              <Percent className="w-2.5 h-2.5" />{' '}
                              {t('sales.dashboard.ruleType.groupFill', { defaultValue: 'Fill' })}
                            </>
                          ) : (
                            <>
                              <GraduationCap className="w-2.5 h-2.5" />{' '}
                              {t('sales.dashboard.ruleType.studentCount', {
                                defaultValue: 'Students',
                              })}
                            </>
                          )}
                        </Badge>
                        <span className="text-muted-foreground">
                          {t('sales.dashboard.target', { defaultValue: 'Target' })}:{' '}
                          <span className="font-semibold text-foreground">
                            {isGroup
                              ? `${rule.targetFillPercentage}%`
                              : `${rule.targetStudentCount}`}
                          </span>
                        </span>
                      </div>

                      {rule.targetDate && (
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>
                            {t('sales.dashboard.deadline', { defaultValue: 'Deadline' })}:{' '}
                            <span className="font-medium text-foreground">
                              {new Date(rule.targetDate).toLocaleDateString()}
                            </span>
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-3 pt-2 border-t border-border/50 text-[10px]">
                        {rule.targetMetAction?.commissionValue != null && (
                          <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            <ArrowUp className="w-2.5 h-2.5" />
                            <span className="font-semibold">
                              +{rule.targetMetAction.commissionValue}%
                            </span>
                          </div>
                        )}
                        {rule.targetNotMetAction?.type === 'reduce' &&
                          rule.targetNotMetAction?.commissionValue != null && (
                            <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ArrowDown className="w-2.5 h-2.5" />
                              <span className="font-semibold">
                                -{rule.targetNotMetAction.commissionValue}%
                              </span>
                            </div>
                          )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Change Password Section */}
      <div className="space-y-4">
        <h4 className="text-sm font-bold  tracking-wider text-muted-foreground px-2">
          {t('profile.changePassword')}
        </h4>
        <Card className="rounded-[2rem] shadow-sm border border-border/60 overflow-hidden bg-card">
          <CardContent className="p-6 md:p-8">
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();

                if (
                  !passwordForm.currentPassword ||
                  !passwordForm.newPassword ||
                  !passwordForm.confirmPassword
                ) {
                  toast.error(t('profile.fillAllFields'));
                  return;
                }

                const errors = validatePassword(passwordForm.newPassword);
                if (errors.length > 0) {
                  toast.error(t('profile.passwordRequirements'));
                  return;
                }

                if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                  toast.error(t('profile.passwordMismatch'));
                  return;
                }

                changePasswordMutation.mutate({
                  currentPassword: passwordForm.currentPassword,
                  newPassword: passwordForm.newPassword,
                  confirmPassword: passwordForm.confirmPassword,
                });
              }}
              className="space-y-5"
            >
              {/* Current Password */}
              <div>
                <label
                  htmlFor="currentPassword"
                  className="text-sm font-bold ltr:ml-1 rtl:mr-1 text-foreground/70 mb-2 block"
                >
                  {t('profile.currentPassword')}
                </label>
                <Input
                  type={showPasswords.current ? 'text' : 'password'}
                  id="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                  }
                  required
                  placeholder="••••••••"
                  className="w-full px-5 py-4 rounded-2xl border-2 border-muted bg-muted/30 text-sm font-semibold text-foreground focus:outline-none focus:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all"
                  rightElement={
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswords({ ...showPasswords, current: !showPasswords.current })
                      }
                      className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      {showPasswords.current ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                />
              </div>

              {/* New Password */}
              <div>
                <label
                  htmlFor="newPassword"
                  className="text-sm font-bold ltr:ml-1 rtl:mr-1 text-foreground/70 mb-2 block"
                >
                  {t('profile.newPassword')}
                </label>
                <Input
                  type={showPasswords.new ? 'text' : 'password'}
                  id="newPassword"
                  value={passwordForm.newPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                    setPasswordErrors(validatePassword(e.target.value));
                  }}
                  required
                  placeholder="••••••••"
                  className="w-full px-5 py-4 rounded-2xl border-2 border-muted bg-muted/30 text-sm font-semibold text-foreground focus:outline-none focus:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all"
                  rightElement={
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswords({ ...showPasswords, new: !showPasswords.new })
                      }
                      className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      {showPasswords.new ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                />

                {/* Password Strength Indicator */}
                {passwordForm.newPassword && (
                  <div className="mt-4 px-1 space-y-2">
                    <div className="flex items-center justify-between text-[10px] font-black  tracking-wider">
                      <span className="text-muted-foreground">{t('profile.passwordStrength')}</span>
                      <span
                        className={`${getPasswordStrength(passwordForm.newPassword)
                          .color.replace('bg-', 'text-')
                          .replace(/border-.*/, '')}`}
                      >
                        {getPasswordStrength(passwordForm.newPassword).label}
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${getPasswordStrength(passwordForm.newPassword).color.replace(/border-.*/, '')}`}
                        style={{
                          width: `${getPasswordStrength(passwordForm.newPassword).strength}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Password Requirements */}
                {passwordForm.newPassword && (
                  <div className="mt-3 px-1 space-y-2">
                    {[
                      {
                        test: passwordForm.newPassword.length >= 8,
                        label: t('profile.passwordMinLength'),
                      },
                      {
                        test: /[A-Z]/.test(passwordForm.newPassword),
                        label: t('profile.passwordUppercase'),
                      },
                      {
                        test: /[a-z]/.test(passwordForm.newPassword),
                        label: t('profile.passwordLowercase'),
                      },
                      {
                        test: /[0-9]/.test(passwordForm.newPassword),
                        label: t('profile.passwordNumber'),
                      },
                      {
                        test: /[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword),
                        label: t('profile.passwordSpecial'),
                      },
                    ].map((req, index) => (
                      <div key={index} className="flex items-center gap-2 text-xs">
                        {req.test ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-muted-foreground/50" />
                        )}
                        <span
                          className={
                            req.test
                              ? 'text-green-600 font-bold'
                              : 'text-muted-foreground font-medium'
                          }
                        >
                          {req.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="text-sm font-bold ltr:ml-1 rtl:mr-1 text-foreground/70 mb-2 block"
                >
                  {t('profile.confirmPassword')}
                </label>
                <Input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                  }
                  required
                  placeholder="••••••••"
                  className="w-full px-5 py-4 rounded-2xl border-2 border-muted bg-muted/30 text-sm font-semibold text-foreground focus:outline-none focus:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all"
                  rightElement={
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })
                      }
                      className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      {showPasswords.confirm ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                />
                {passwordForm.confirmPassword &&
                  passwordForm.newPassword !== passwordForm.confirmPassword && (
                    <p className="mt-2 ml-1 text-xs text-red-600 font-bold flex items-center gap-1">
                      <XCircle className="h-3.5 w-3.5" />
                      {t('profile.passwordMismatch')}
                    </p>
                  )}
                {passwordForm.confirmPassword &&
                  passwordForm.newPassword === passwordForm.confirmPassword && (
                    <p className="mt-2 ml-1 text-xs text-green-600 font-bold flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t('profile.passwordMatch')}
                    </p>
                  )}
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={
                    changePasswordMutation.isPending ||
                    passwordErrors.length > 0 ||
                    passwordForm.newPassword !== passwordForm.confirmPassword
                  }
                  className="w-full sm:w-auto px-8 py-6 rounded-2xl bg-primary text-primary-foreground font-black text-sm hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 cursor-pointer"
                >
                  {changePasswordMutation.isPending ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <ShieldAlert className="mr-2 h-5 w-5" />
                  )}
                  {t('profile.updatePassword')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Active Sessions Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-2">
          <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            {t('profile.activeSessions')}
          </h4>
          {sessionsResponse?.data && sessionsResponse.data.length > 1 && (
            <button
              onClick={() => {
                openDeleteConfirm({
                  title: t('profile.terminateAllOther'),
                  description: t('profile.confirmTerminateAll'),
                  onConfirm: () => terminateAllOtherMutation.mutateAsync(),
                });
              }}
              disabled={terminateAllOtherMutation.isPending}
              className="text-xs font-bold text-destructive hover:underline disabled:opacity-50 cursor-pointer"
            >
              {t('profile.terminateAllOther')}
            </button>
          )}
        </div>

        <Card className="rounded-[2rem] shadow-sm border border-border/60 overflow-hidden bg-card">
          <div className="flex flex-col divide-y divide-border/40">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              sortedSessions.map((session: ActiveLoginSession) => (
                <div
                  key={session.id}
                  className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 transition-colors hover:bg-muted/30 ${session.isCurrent ? 'bg-primary/5 hover:bg-primary/10' : ''}`}
                >
                  <div className="flex items-start gap-4 mb-4 sm:mb-0">
                    <div
                      className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 shadow-xs ${session.isCurrent ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}
                    >
                      {getDeviceIcon(session.deviceType || session.os)}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-base text-foreground">
                          {session.deviceName || session.os || 'Unknown Device'}
                        </p>
                        {session.isCurrent && (
                          <Badge
                            variant="default"
                            className="bg-green-600 hover:bg-green-700 shadow-xs px-2 py-0 text-[10px] uppercase tracking-wider"
                          >
                            {t('profile.currentSession')}
                          </Badge>
                        )}
                        {session.portal && (
                          <Badge
                            variant="outline"
                            className="capitalize text-[10px] px-2 py-0 border-border/50 text-muted-foreground"
                          >
                            {session.portal.replace('-', ' ')}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">
                        <p className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-foreground/80">{session.browser}</span> on{' '}
                          {session.os}
                          <span className="text-muted-foreground/30 px-0.5 hidden sm:inline">
                            •
                          </span>
                          <span className="block sm:inline w-full sm:w-auto">
                            {session.ipAddress}
                          </span>
                        </p>
                        <p className="flex items-center gap-1.5 pt-1 flex-wrap">
                          {session.location !== 'Unknown' && (
                            <>
                              <span className="text-foreground/70">{session.location}</span>
                              <span className="text-muted-foreground/30">•</span>
                            </>
                          )}
                          <span>
                            {t('profile.lastActive')}: {formatDate(session.lastActivityAt)}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {!session.isCurrent && (
                    <button
                      onClick={() => {
                        openDeleteConfirm({
                          title: t('common.revoke'),
                          description: t('profile.confirmTerminate'),
                          itemName: `${session.browser} (${session.os})`,
                          onConfirm: () => terminateSessionMutation.mutateAsync(session.id),
                        });
                      }}
                      disabled={terminateSessionMutation.isPending}
                      className="w-full sm:w-auto p-2.5 rounded-xl text-destructive bg-destructive/5 hover:bg-destructive/15 transition-colors cursor-pointer flex items-center justify-center border border-destructive/10"
                      title={t('common.revoke')}
                    >
                      <Trash2 className="h-5 w-5 mr-0 sm:mr-0" />
                      <span className="ml-2 sm:hidden font-bold">{t('common.revoke')}</span>
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
